import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Select from 'react-select';
import { compareObjects } from '@lib/query-utils';
import { getSiteURL } from '@lib/utils';

// ------------------------------------------------------

import Button from '@components/button';
import Container from '@layout/container';
import Layout from '@layout/index';
import TabSlider from '@components/tab-slider';

// ------------------------------------------------------

import {
  second_row_filters,
  label,
  submit_button,
  is_mobile,
  is_desktop
} from './styles.module.scss';

// ------------------------------------------------------

const INITIAL_PARAMS = {
  days: '7',
  location_type: 'city',
  search_type: 'new_listings',
  state: 'OH'
};

// ------------------------------------------------------

const STATES = [
  { label: 'Ohio', value: 'OH' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Indiana', value: 'IN' }
];
const DAYS_GONE = ['1', '2', '3', '4', '5', '6', '7'];
const LOCATION_TYPES = [
  { label: 'Cities/Suburbs', value: 'city' },
  { label: 'Counties', value: 'county' },
  { label: 'School Districts', value: 'school_district' },
  { label: 'Zip Codes', value: 'zip' }
];
const SEARCH_TYPES = [
  { label: 'New Listings', value: 'new_listings' },
  { label: 'Price Changes', value: 'price_changes' },
  { label: 'Open Houses', value: 'open_houses' },
  { label: 'New Construction', value: 'new_construction' },
  { headerLabel: 'For Sale', label: 'All Listings', value: 'all_listings' }
];

const BrowseSelections = () => {
  const router = useRouter();
  const currQuery = router.query;

  // ------------------------------------------------------

  const [searchType, setSearchType] = useState(currQuery?.search_type || SEARCH_TYPES[0].value);
  const [locationType, setLocationType] = useState(
    currQuery?.location_type || LOCATION_TYPES[0].value
  );
  const [selectedState, setSelectedState] = useState(currQuery?.state || STATES[0].value);
  const [daysGone, setDaysGone] = useState(currQuery?.days || '7');

  // ------------------------------------------------------

  const needsDaysGone = searchType === 'new_listings' || searchType === 'price_changes';

  // ------------------------------------------------------

  const defaultSearchType = SEARCH_TYPES.find((type) => type.value === searchType);
  const defaultLocationType = LOCATION_TYPES.find((loc) => loc.value === locationType);

  // ------------------------------------------------------

  useEffect(() => {
    if (!router.isReady) return;
    setSearchType(currQuery?.search_type || SEARCH_TYPES[0].value);
    setLocationType(currQuery?.location_type || LOCATION_TYPES[0].value);
    setSelectedState(currQuery?.state || STATES[0].value);
    setDaysGone(currQuery?.days || '7');
  }, [router.isReady]);

  useEffect(() => {
    if (!router.isReady) return;

    let queryParams = {
      days: daysGone,
      location_type: locationType,
      search_type: searchType,
      state: selectedState
    };

    queryParams = compareObjects(queryParams, INITIAL_PARAMS);

    router.push({ pathname: router.pathname, query: queryParams }, undefined, {
      shallow: true
    });
  }, [searchType, locationType, selectedState, daysGone]);

  // ------------------------------------------------------

  const getBrowseUrl = () => {
    const daysSuffix = needsDaysGone ? `/${daysGone}` : '';
    return `/browse/${locationType}/${selectedState}/${searchType}${daysSuffix}`;
  };

  const generateHiddenUrls = () => {
    const urls = [];
    STATES.forEach((state) => {
      LOCATION_TYPES.forEach((locationType) => {
        SEARCH_TYPES.forEach((searchType) => {
          if (['price_changes', 'new_listings'].includes(searchType.value)) {
            DAYS_GONE.forEach((days) => {
              urls.push(`/browse/${locationType.value}/${state.value}/${searchType.value}/${days}`);
            });
          } else {
            urls.push(`/browse/${locationType.value}/${state.value}/${searchType.value}`);
          }
        });
      });
    });
    return urls;
  };

  const hiddenUrls = generateHiddenUrls();

  return (
    <Layout canonicalUrl={getSiteURL() + router.pathname}>
      <Container
        backgroundImage={{
          height: '216px',
          src: '/fpo/FPO_Background_header.png',
          width: '1440px'
        }}
        backgroundImageLayout="responsive"
        gridOptions={{ layout: 'single' }}
        priority
        spacing="small"
        theme="has-background-image-with-content-box-shadow"
      >
        <h1>Browse the Market</h1>
        <p>
          Choose from the options below for an A-Z list of locations or school districts with
          properties matching the criteria you selected.
        </p>

        <div className={is_mobile}>
          <span className={label}>Select Search Type</span>
          <Select
            disabled={!router.isReady}
            defaultValue={defaultSearchType}
            options={SEARCH_TYPES}
            onChange={(search) => setSearchType(search.value)}
          />
        </div>

        <div className={is_desktop}>
          <TabSlider
            label="Select Search Type"
            items={SEARCH_TYPES.map(({ label, value }) => ({
              label,
              value
            }))}
            defaultSelection={currQuery?.search_type}
            setSelectedTabCallback={setSearchType}
          />
        </div>

        <div className={second_row_filters}>
          <TabSlider
            label="Select State"
            items={STATES.map(({ label, value }) => {
              return {
                label,
                value
              };
            })}
            defaultSelection={currQuery?.state}
            setSelectedTabCallback={setSelectedState}
          />

          {needsDaysGone && (
            <TabSlider
              defaultSelection={currQuery?.days || '7'}
              label={'Select Days Back'}
              items={DAYS_GONE.map((day) => {
                return {
                  label: day,
                  value: day
                };
              })}
              setSelectedTabCallback={setDaysGone}
            />
          )}
        </div>

        <div className={is_mobile}>
          <span className={label}>Select Location Type</span>
          <Select
            disabled={!router.isReady}
            defaultValue={defaultLocationType}
            options={LOCATION_TYPES}
            onChange={(loc) => setLocationType(loc.value)}
          />
        </div>

        <div className={is_desktop}>
          <TabSlider
            label="Select Location Type"
            items={LOCATION_TYPES.map(({ label, value }) => ({
              label,
              value
            }))}
            defaultSelection={currQuery?.location_type}
            setSelectedTabCallback={setLocationType}
          />
        </div>

        <div className={submit_button}>
          <Button label="Browse A-Z" url={getBrowseUrl()} theme="red" />
        </div>

        <div style={{ display: 'none' }}>
          {hiddenUrls.map((url, index) => (
            <a key={index} href={url}>
              {url}
            </a>
          ))}
        </div>
      </Container>
    </Layout>
  );
};

export default BrowseSelections;
